import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { TeacherOnboardingPagesEnum } from '../TeacherOnboarding';
import { getUSStates, getStateCode } from '../../../config/usa-states';
import BackButton from '../../../components/Common/BackButton';
import { setTeacherOnboarding } from '../../../store/userSlice';
import { RootState } from '../../../store/store';
import {
  saveTeacherSurvey,
  searchSchools,
  createSchool,
  checkSchoolsAvailable,
  SchoolSearchResult,
} from '../services/Teacher.service';
import { getActiveCountries, getDefaultCountry, CountryOption } from '../services/Country.service';

const SEARCH_DEBOUNCE_MS = 300;

export interface OnboardingSchoolInfoProps {
  onSuccess?: () => void;
  onBack?: () => void;
}

const OnboardingSchoolInfo: React.FC<OnboardingSchoolInfoProps> = ({ onSuccess, onBack }) => {
  const navigateTo = useNavigate();
  const dispatch = useDispatch();
  const teacherOnboarding = useSelector((state: RootState) => state.user.teacherOnboarding);
  const [exitAnimation, setExitAnimation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const usStates = getUSStates();

  // ── Country state ──────────────────────────────────────────────────────
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<CountryOption | null>(null);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  // ── School availability per country ───────────────────────────────────
  const [schoolsAvailableForCountry, setSchoolsAvailableForCountry] = useState<boolean>(true);
  const [schoolsCheckLoading, setSchoolsCheckLoading] = useState(false);

  // ── State / school state ───────────────────────────────────────────────
  const [selectedState, setSelectedState] = useState(teacherOnboarding?.state || '');
  const [addSchoolState, setAddSchoolState] = useState(teacherOnboarding?.state || '');
  const [city, setCity] = useState(teacherOnboarding?.city || '');
  const [schoolName, setSchoolName] = useState(teacherOnboarding?.schoolName || '');
  const [schoolId, setSchoolId] = useState<string | undefined>(teacherOnboarding?.schoolId);

  const [schoolSearchQuery, setSchoolSearchQuery] = useState(teacherOnboarding?.schoolName || '');
  const [schoolResults, setSchoolResults] = useState<SchoolSearchResult[]>([]);
  const [schoolSearchOpen, setSchoolSearchOpen] = useState(false);
  const [schoolSearchLoading, setSchoolSearchLoading] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const schoolSearchRef = useRef<HTMLDivElement>(null);
  const skipNextSearchRef = useRef(false);

  const [isAddingSchool, setIsAddingSchool] = useState(false);
  const [addSchoolName, setAddSchoolName] = useState('');
  const [addSchoolDistrict, setAddSchoolDistrict] = useState('');
  const [addSchoolCity, setAddSchoolCity] = useState('');
  const [addSchoolSubmitting, setAddSchoolSubmitting] = useState(false);

  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
  const stateDropdownRef = useRef<HTMLDivElement>(null);

  const isUSA = selectedCountry?.code === 'US';

  // ── Load countries on mount ────────────────────────────────────────────
  useEffect(() => {
    setExitAnimation(false);

    const loadCountries = async () => {
      const [active, defaultCountry] = await Promise.all([
        getActiveCountries(),
        getDefaultCountry(),
      ]);
      setCountries(active);

      // Preselect: if already in Redux use that, else use admin default
      if (teacherOnboarding?.country) {
        const match = active.find((c) => c.code === teacherOnboarding.country);
        if (match) {
          setSelectedCountry(match);
          return;
        }
      }
      if (defaultCountry) {
        const match = active.find((c) => c.code === defaultCountry.code) ?? defaultCountry;
        setSelectedCountry(match);
        dispatch(setTeacherOnboarding({ data: { country: match.code } }));
      } else if (active.length > 0) {
        setSelectedCountry(active[0]);
        dispatch(setTeacherOnboarding({ data: { country: active[0].code } }));
      }
    };

    loadCountries();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── When country changes: check school availability + clear dependent fields ──
  useEffect(() => {
    if (!selectedCountry) return;

    const check = async () => {
      setSchoolsCheckLoading(true);
      const hasAny = await checkSchoolsAvailable(selectedCountry.code);
      setSchoolsAvailableForCountry(hasAny);
      setSchoolsCheckLoading(false);

      // If no schools available for this country → jump straight to add-school form
      if (!hasAny) setIsAddingSchool(true);
      else setIsAddingSchool(false);
    };

    check();

    // Clear school-related state when country changes
    setSelectedState('');
    setAddSchoolState('');
    setSchoolName('');
    setSchoolId(undefined);
    setSchoolSearchQuery('');
    setSchoolResults([]);
  }, [selectedCountry]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Click-outside for dropdowns ────────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setIsCountryDropdownOpen(false);
      }
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target as Node)) {
        setIsStateDropdownOpen(false);
      }
      if (schoolSearchRef.current && !schoolSearchRef.current.contains(event.target as Node)) {
        setSchoolSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const runSchoolSearch = useCallback(async (q: string) => {
    if (!q || q.length < 2) {
      setSchoolResults([]);
      return;
    }
    setSchoolSearchLoading(true);
    const stateCode = isUSA && selectedState ? getStateCode(selectedState) : undefined;
    const countryCode = selectedCountry?.code;
    const results = await searchSchools(q.trim(), stateCode, countryCode);
    setSchoolResults(results);
    setSchoolSearchLoading(false);
    setSchoolSearchOpen(true);
  }, [isUSA, selectedState, selectedCountry]);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }
    if (schoolSearchQuery.length < 2) {
      setSchoolResults([]);
      setSchoolSearchOpen(false);
      return;
    }
    searchTimeoutRef.current = setTimeout(() => {
      runSchoolSearch(schoolSearchQuery);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [schoolSearchQuery, runSchoolSearch]);

  const handleSelectSchool = (school: SchoolSearchResult) => {
    skipNextSearchRef.current = true;
    setSchoolName(school.name);
    setSchoolId(school.id);
    setCity(school.city ?? '');
    setSchoolSearchQuery(school.name);
    setSchoolSearchOpen(false);
    setSchoolResults([]);
    if (isUSA) setSelectedState(school.state ?? '');
  };

  const persistSchoolStepAndAdvance = async (data: {
    country: string;
    state: string;
    city?: string;
    schoolName: string;
    schoolId?: string;
  }) => {
    dispatch(setTeacherOnboarding({ data }));
    return saveTeacherSurvey({ ...teacherOnboarding, ...data }, () => {
      dispatch(setTeacherOnboarding({ data: { ...teacherOnboarding, ...data } }));
      setExitAnimation(true);
      setTimeout(() => {
        if (onSuccess) onSuccess();
        else navigateTo(TeacherOnboardingPagesEnum.OnboardingClassInfo);
      }, 250);
    });
  };

  const handleContinue = async () => {
    if (!schoolName.trim() || isSubmitting) return;
    if (isUSA && !selectedState) return;
    setIsSubmitting(true);
    const data = {
      country: selectedCountry?.code ?? 'US',
      state: selectedState,
      city: city.trim() || undefined,
      schoolName: schoolName.trim(),
      schoolId: schoolId || undefined,
    };
    try {
      const ok = await persistSchoolStepAndAdvance(data);
      if (!ok) setIsSubmitting(false);
    } catch {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setExitAnimation(true);
    setTimeout(() => {
      if (onBack) onBack();
      else navigateTo('/classroom/dashboard');
    }, 250);
  };

  const handleAddSchoolContinue = async () => {
    if (!addSchoolName.trim() || addSchoolSubmitting) return;
    if (isUSA && !addSchoolState) return;
    setAddSchoolSubmitting(true);

    const stateValue = isUSA ? getStateCode(addSchoolState) : addSchoolState.trim() || '';
    const created = await createSchool({
      name: addSchoolName.trim(),
      state: stateValue || undefined,
      country: selectedCountry?.code ?? 'US',
      districtName: addSchoolDistrict.trim() || undefined,
      city: addSchoolCity.trim() || undefined,
    });
    if (!created) {
      setAddSchoolSubmitting(false);
      return;
    }

    const data = {
      country: selectedCountry?.code ?? 'US',
      state: isUSA ? addSchoolState : addSchoolState.trim(),
      city: addSchoolCity.trim() || undefined,
      schoolName: created.name.trim(),
      schoolId: created.id,
    };

    try {
      const ok = await persistSchoolStepAndAdvance(data);
      if (!ok) setAddSchoolSubmitting(false);
    } catch {
      setAddSchoolSubmitting(false);
    }
  };

  const isFormValid = schoolName.trim() && (!isUSA || selectedState);

  // ── Country dropdown (reused in both views) ────────────────────────────
  const CountrySelector = (
    <div className="relative" ref={countryDropdownRef}>
      <h2 className="text-base sm:text-lg font-bold text-storyblackText mb-2">
        Country <span className="text-storyseconderror">*</span>
      </h2>
      <div
        className={`w-full p-3 border border-secondaryStoryLightGrayInputBorder rounded-lg bg-white flex items-center justify-between relative ${
          schoolsCheckLoading ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'
        }`}
        onClick={() => !schoolsCheckLoading && setIsCountryDropdownOpen(!isCountryDropdownOpen)}
      >
        <div className="truncate w-full">
          {selectedCountry ? (
            <span className="text-sm sm:text-base">{selectedCountry.name}</span>
          ) : (
            <span className="text-sm sm:text-base opacity-50">Select your country</span>
          )}
        </div>
        <div className="pointer-events-none flex items-center px-1 text-gray-700 shrink-0">
          {schoolsCheckLoading ? (
            <svg className="w-4 h-4 sm:w-5 sm:h-5 animate-spin text-secondaryStoryAdditionalLightBlueText" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          ) : (
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          )}
        </div>
      </div>
      {isCountryDropdownOpen && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-secondaryStoryLightGrayInputBorder rounded-lg shadow-lg max-h-60 overflow-y-auto">
          <div className="py-1">
            {countries.map((country) => (
              <div
                key={country.code}
                className={`px-4 py-2 cursor-pointer hover:bg-gray-100 text-center ${selectedCountry?.code === country.code ? 'bg-gray-50' : ''}`}
                onClick={() => {
                  setSelectedCountry(country);
                  dispatch(setTeacherOnboarding({ data: { country: country.code } }));
                  setIsCountryDropdownOpen(false);
                }}
              >
                {country.name}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // ── Add-school form (manual entry) ─────────────────────────────────────
  if (isAddingSchool) {
    return (
      <div className={`max-w-full mx-auto px-4 py-6 sm:py-8 transition-all duration-500 ${exitAnimation ? 'opacity-0 transform translate-y-10' : 'opacity-100'}`}>
        <h2 className="text-base sm:text-lg font-bold text-storyblackText mb-4">Add your school</h2>
        <div className="space-y-4 text-left">
          {CountrySelector}

          <div className={schoolsCheckLoading ? 'opacity-50 pointer-events-none' : ''}>
          <div>
            <label className="block text-base font-bold text-storyblackText mb-2">
              School name <span className="text-storyseconderror">*</span>
            </label>
            <input
              type="text"
              value={addSchoolName}
              onChange={(e) => setAddSchoolName(e.target.value)}
              placeholder="School name"
              className="w-full p-3 border border-secondaryStoryLightGrayInputBorder rounded-lg focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText text-sm sm:text-base"
              aria-label="School name"
            />
          </div>

          <div>
            <label className="block text-base font-bold text-storyblackText mb-2">School district</label>
            <input
              type="text"
              value={addSchoolDistrict}
              onChange={(e) => setAddSchoolDistrict(e.target.value)}
              placeholder="School district"
              className="w-full p-3 border border-secondaryStoryLightGrayInputBorder rounded-lg focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText text-sm sm:text-base"
              aria-label="School district"
            />
          </div>

          <div>
            <label className="block text-base font-bold text-storyblackText mb-2">City or Town</label>
            <input
              type="text"
              value={addSchoolCity}
              onChange={(e) => setAddSchoolCity(e.target.value)}
              placeholder="City or Town"
              className="w-full p-3 border border-secondaryStoryLightGrayInputBorder rounded-lg focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText text-sm sm:text-base"
              aria-label="City or Town"
            />
          </div>

          <div>
            {isUSA ? (
              <>
                <label className="block text-base font-bold text-storyblackText mb-2">
                  State <span className="text-storyseconderror">*</span>
                </label>
                <select
                  value={addSchoolState}
                  onChange={(e) => setAddSchoolState(e.target.value)}
                  className="w-full p-3 border border-secondaryStoryLightGrayInputBorder rounded-lg focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText text-sm sm:text-base bg-white"
                  aria-label="State"
                >
                  <option value="">Select your state</option>
                  {usStates.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </>
            ) : (
              <>
                <label className="block text-base font-bold text-storyblackText mb-2">
                  State / Province / Region
                </label>
                <input
                  type="text"
                  value={addSchoolState}
                  onChange={(e) => setAddSchoolState(e.target.value)}
                  placeholder="State, province or region (optional)"
                  className="w-full p-3 border border-secondaryStoryLightGrayInputBorder rounded-lg focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText text-sm sm:text-base"
                  aria-label="State or province"
                />
              </>
            )}
          </div>
          </div>
        </div>

        <div className="flex flex-row items-center mt-8 gap-4 justify-between">
          <BackButton
            onClick={() => {
              if (schoolsAvailableForCountry) setIsAddingSchool(false);
              else handleBack();
            }}
          />
          <button
            className={`px-6 py-2 h-9 bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow text-sm sm:text-base ${addSchoolName.trim() && (!isUSA || addSchoolState) && !addSchoolSubmitting ? '' : 'opacity-50 cursor-not-allowed'}`}
            onClick={handleAddSchoolContinue}
            disabled={!addSchoolName.trim() || (isUSA && !addSchoolState) || addSchoolSubmitting}
          >
            {addSchoolSubmitting ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </div>
    );
  }

  // ── Main search form ───────────────────────────────────────────────────
  return (
    <div className={`max-w-full mx-auto px-4 py-6 sm:py-8 transition-all duration-500 ${exitAnimation ? 'opacity-0 transform translate-y-10' : 'opacity-100'}`}>
      <div className="space-y-6 text-left">
        {CountrySelector}

        <div className={schoolsCheckLoading ? 'opacity-50 pointer-events-none' : ''}>
            {isUSA ? (
              <div className="relative" ref={stateDropdownRef}>
                <h2 className="text-base sm:text-lg font-bold text-storyblackText mb-2">
                  State <span className="text-storyseconderror">*</span>
                </h2>
                <div
                  className="w-full p-3 border border-secondaryStoryLightGrayInputBorder rounded-lg bg-white cursor-pointer flex items-center justify-between relative"
                  onClick={() => setIsStateDropdownOpen(!isStateDropdownOpen)}
                >
                  <div className="truncate w-full">
                    {selectedState ? (
                      <span className="text-sm sm:text-base">{selectedState}</span>
                    ) : (
                      <span className="text-sm sm:text-base opacity-50">Select your state</span>
                    )}
                  </div>
                  <div className="pointer-events-none flex items-center px-1 text-gray-700 shrink-0">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                {isStateDropdownOpen && (
                  <div className="absolute z-20 mt-1 w-full bg-white border border-secondaryStoryLightGrayInputBorder rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    <div className="py-1">
                      {usStates.map((s) => (
                        <div
                          key={s}
                          className={`px-4 py-2 cursor-pointer hover:bg-gray-100 text-center ${selectedState === s ? 'bg-gray-50' : ''}`}
                          onClick={() => {
                            setSelectedState(s);
                            setIsStateDropdownOpen(false);
                          }}
                        >
                          {s}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <h2 className="text-base sm:text-lg font-bold text-storyblackText mb-2">
                  State / Province / Region
                </h2>
                <input
                  type="text"
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  placeholder="State, province or region (optional)"
                  className="w-full p-3 border border-secondaryStoryLightGrayInputBorder rounded-lg focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText text-sm sm:text-base"
                  aria-label="State or province"
                />
              </div>
            )}

            <div className="relative" ref={schoolSearchRef}>
              <h2 className="text-base sm:text-lg font-bold text-storyblackText mb-2">
                School <span className="text-storyseconderror">*</span>
              </h2>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </span>
                <input
                  type="text"
                  value={schoolSearchQuery}
                  onChange={(e) => setSchoolSearchQuery(e.target.value)}
                  onFocus={() => schoolSearchQuery.length >= 2 && setSchoolSearchOpen(true)}
                  placeholder="Search for your school"
                  className="w-full pl-10 pr-3 py-3 border border-secondaryStoryLightGrayInputBorder rounded-lg focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText text-sm sm:text-base"
                  aria-label="Search for your school"
                />
              </div>
              {schoolSearchLoading && (
                <p className="text-xs text-secondaryStoryGrayButtonText mt-1">Searching...</p>
              )}
              {schoolSearchOpen && schoolResults.length > 0 && (
                <div className="absolute z-20 mt-1 w-full bg-white border border-secondaryStoryLightGrayInputBorder rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  <div className="py-1 px-2 text-xs text-secondaryStoryGrayButtonText font-medium">Results</div>
                  {schoolResults.map((school) => (
                    <button
                      key={school.id}
                      type="button"
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                      onClick={() => handleSelectSchool(school)}
                    >
                      <div className="font-semibold text-storyblackText">{school.name}</div>
                      {(school.address || school.city || school.state || school.zip) && (
                        <div className="text-xs text-gray-400 mt-0.5">
                          {[school.address, [school.state, school.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                          {school.city ? ` - ${school.city}` : ''}
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-xs text-secondaryStoryGrayButtonText mt-1">
                Don&apos;t see your school?{' '}
                <button
                  type="button"
                  className="text-secondaryStoryAdditionalLightBlueText font-semibold hover:underline"
                  onClick={() => setIsAddingSchool(true)}
                >
                  Add your school
                </button>
              </p>
            </div>
        </div>
      </div>

      <div className={`flex flex-row items-center mt-8 sm:mt-10 gap-4 sm:gap-0 ${onBack ? 'justify-between' : 'justify-end'}`}>
        {onBack && (
          <div>
            <BackButton onClick={handleBack} />
          </div>
        )}
        <button
          className={`px-6 py-2 h-9 bg-secondaryStoryAdditionalLightBlueText text-whiteText font-bold leading-none rounded-2xl hover:shadow-md transition-shadow flex items-center text-sm sm:text-base w-auto justify-center ${isFormValid && !isSubmitting ? '' : 'opacity-50 cursor-not-allowed'}`}
          onClick={handleContinue}
          disabled={!isFormValid || isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Continue'}
        </button>
      </div>
    </div>
  );
};

export default OnboardingSchoolInfo;
